import React from "react";
import {
    BoardContainer, BoardColumn, BoardColumnHeader, BoardColumnBody,
    PatientCard, EmptyBoard, LoadingWrapper,
} from "./LabourWardStyles";

/**
 * LabourWardBoard — Kanban-style board showing labour ward patients
 * Three columns: Admitted → In Labour → Delivered
 */
export const LabourWardBoard = function(props) {
    const admissions = props.admissions;
    const loadingBoard = props.loadingBoard;
    const boardStats = props.boardStats;
    const updateStatus = props.updateStatus;

    if (loadingBoard) {
        return React.createElement(LoadingWrapper, null,
            React.createElement("span", null, "⏳"),
            " Loading labour ward..."
        );
    }

    const formatDate = function(dateStr) {
        if (!dateStr) return "";
        try {
            const d = new Date(dateStr);
            const pad = function(n) { return n < 10 ? "0" + n : "" + n; };
            return pad(d.getDate()) + "/" + pad(d.getMonth() + 1) + " " +
                pad(d.getHours()) + ":" + pad(d.getMinutes());
        } catch (e) { return dateStr; }
    };

    const getRiskClass = function(riskFactors) {
        if (!riskFactors || !Array.isArray(riskFactors)) return "low";
        if (riskFactors.length >= 3) return "high";
        if (riskFactors.length > 0) return "medium";
        return "low";
    };

    const admittedList = admissions.filter(function(a) { return a.status === "admitted"; });
    const inLabourList = admissions.filter(function(a) { return a.status === "in_labour"; });
    const deliveredList = admissions.filter(function(a) { return a.status === "delivered"; });

    const renderCard = function(admission) {
        const riskClass = getRiskClass(admission.risk_factors);
        return React.createElement(PatientCard, { key: admission.id },
            React.createElement("div", { className: "pc-header" },
                React.createElement("span", { className: "pc-name" },
                    admission.patient_name || "Patient #" + admission.patient_number
                ),
                React.createElement("span", { className: "pc-time" },
                    formatDate(admission.admission_datetime)
                )
            ),
            React.createElement("div", { className: "pc-meta" },
                admission.gestational_age_weeks
                    ? React.createElement("span", { className: "pc-tag" },
                        "GA: " + admission.gestational_age_weeks + "w"
                    ) : null,
                admission.onset_type
                    ? React.createElement("span", { className: "pc-tag" },
                        admission.onset_type.replace(/_/g, " ")
                    ) : null,
                admission.initial_cervical_dilation_cm
                    ? React.createElement("span", { className: "pc-tag" },
                        "Cx: " + admission.initial_cervical_dilation_cm + "cm"
                    ) : null,
                React.createElement("span", { className: "pc-risk " + riskClass },
                    riskClass.charAt(0).toUpperCase() + riskClass.slice(1) + " risk"
                )
            ),
            admission.fetal_presentation && admission.fetal_presentation !== "cephalic"
                ? React.createElement("div", {
                    style: { marginTop: 6, fontSize: "0.72rem", color: "#e65100", fontWeight: 600 }
                }, "⚠ " + admission.fetal_presentation + " presentation")
                : null
        );
    };

    const renderColumn = function(variant, title, list) {
        return React.createElement(BoardColumn, null,
            React.createElement(BoardColumnHeader, { variant: variant },
                React.createElement("span", null, title),
                React.createElement("span", { className: "col-count" }, list.length)
            ),
            React.createElement(BoardColumnBody, null,
                list.length === 0
                    ? React.createElement(EmptyBoard, null, "No patients")
                    : list.map(renderCard)
            )
        );
    };

    return React.createElement("div", null,
        /* Summary stats */
        React.createElement("div", {
            style: {
                display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap",
            }
        },
            React.createElement("div", {
                style: {
                    padding: "10px 18px", borderRadius: 8, background: "#e8eaf6",
                    fontSize: "0.84rem", fontWeight: 600, color: "#1a237e",
                    display: "flex", alignItems: "center", gap: 6,
                }
            }, "🏥 Total: ", React.createElement("strong", null, boardStats.total)),
            React.createElement("div", {
                style: {
                    padding: "10px 18px", borderRadius: 8, background: "#e8eaf6",
                    fontSize: "0.84rem", fontWeight: 600, color: "#3949ab",
                    display: "flex", alignItems: "center", gap: 6,
                }
            }, "🛏 Admitted: ", React.createElement("strong", null, boardStats.admitted)),
            React.createElement("div", {
                style: {
                    padding: "10px 18px", borderRadius: 8, background: "#fff3e0",
                    fontSize: "0.84rem", fontWeight: 600, color: "#e65100",
                    display: "flex", alignItems: "center", gap: 6,
                }
            }, "⏱ In Labour: ", React.createElement("strong", null, boardStats.in_labour)),
            React.createElement("div", {
                style: {
                    padding: "10px 18px", borderRadius: 8, background: "#e8f5e9",
                    fontSize: "0.84rem", fontWeight: 600, color: "#2e7d32",
                    display: "flex", alignItems: "center", gap: 6,
                }
            }, "👶 Delivered: ", React.createElement("strong", null, boardStats.delivered))
        ),

        /* Board columns */
        React.createElement(BoardContainer, null,
            renderColumn("admitted", "🛏 Admitted", admittedList),
            renderColumn("in_labour", "⏱ In Active Labour", inLabourList),
            renderColumn("delivered", "👶 Delivered Today", deliveredList)
        ),

        /* Empty state */
        admissions.length === 0
            ? React.createElement("div", {
                style: {
                    textAlign: "center", padding: "40px 20px", color: "#90a4ae",
                    fontSize: "0.9rem",
                }
            },
                React.createElement("div", { style: { fontSize: "2.5rem", marginBottom: 12 } }, "🏥"),
                React.createElement("div", { style: { fontWeight: 600, color: "#546e7a" } },
                    "No patients currently in the labour ward"
                ),
                React.createElement("div", { style: { fontSize: "0.82rem", marginTop: 6 } },
                    "Patients will appear here once admitted to the labour ward"
                )
            ) : null
    );
};
